<template>
    <div class="relative isolate bg-white px-6 py-24 sm:py-32 lg:px-8">
        <div class="absolute inset-x-0 -top-3 -z-10 transform-gpu overflow-hidden px-36 blur-3xl" aria-hidden="true">
            <div class="mx-auto aspect-[1155/678] w-[72.1875rem] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30"
                style="clip-path: polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)" />
        </div>
        <div class="mx-auto max-w-4xl text-center">
            <h2 class="text-base/7 font-semibold text-emerald-600">Pricing</h2>
            <p class="mt-2 text-balance text-5xl font-semibold tracking-tight text-gray-900 sm:text-6xl">Choose the
                right plan for you</p>
        </div>
        <p class="mx-auto mt-6 max-w-2xl text-pretty text-center text-lg font-medium text-gray-600 sm:text-xl/8">
            Unlock your potential in Arabic with flexible plans for learners and educators.
            Whether you're just starting or looking to advance, our pricing ensures you get the resources and support
            you need to succeed.
        </p>
        <div>
            <Tabs :value=value>
                <div class="max-w-7xl flex justify-center items-center mx-auto mt-16">
                    <TabList>
                        <Tab value="0" as="div" class="flex items-center gap-2">
                            <span class="font-bold whitespace-nowrap">30 Minutes</span>
                        </Tab>
                        <Tab value="1" as="div" class="flex items-center gap-2">
                            <span class="font-bold whitespace-nowrap">60 Minutes</span>
                        </Tab>
                    </TabList>
                </div>
                <TabPanels>
                    <TabPanel value="0" as="p" class="m-0">
                        <div
                            class="mx-auto mt-16 grid max-w-7lg grid-cols-1 items-center gap-y-6 sm:mt-20 sm:gap-y-0 lg:max-w-7xl lg:grid-cols-4">
                            <!-- Pricing tiers -->
                            <div v-for="(tier, tierIdx) in tiers[0]?.tiers" :key="tier.id" :class="[
                                    'bg-white/60 sm:mx-8 lg:mx-0',
                                    tierIdx === 0 ? 'rounded-t-3xl sm:rounded-b-none lg:rounded-bl-3xl lg:rounded-tr-none' : '',
                                    tierIdx === 3 ? 'rounded-t-3xl sm:rounded-t-none lg:rounded-tr-3xl lg:rounded-bl-none' : '',
                                    tierIdx !== 0 && tierIdx !== 3 ? 'lg:rounded-none' : '',
                                    'rounded-3xl p-8 ring-1 ring-gray-900/10 sm:p-10']">
                                <h3 :id="tier.id"
                                    :class="[tier.featured ? 'text-emerald-400' : 'text-emerald-600', 'text-base/7 font-semibold']">
                                    {{
                                    tier.name }}</h3>
                                <p class="mt-4 flex items-baseline gap-x-2">
                                    <span
                                        :class="[tier.featured ? 'text-white' : 'text-gray-900', 'text-5xl font-semibold tracking-tight']">
                                        ${{ Number(tier.price) }}</span>
                                    <span
                                        :class="[tier.featured ? 'text-gray-400' : 'text-gray-500', 'text-base']">/month</span>
                                </p>
                                <p :class="[tier.featured ? 'text-gray-300' : 'text-gray-600', 'mt-6 text-base/7']">
                                    {{
                                    tier.description
                                    }}</p>
                                <ul role="list"
                                    :class="[tier.featured ? 'text-gray-300' : 'text-gray-600', 'mt-8 space-y-3 text-sm/6 sm:mt-10']">
                                    <li v-for="feature in tier.features" :key="feature" class="flex gap-x-3">
                                        <CheckIcon
                                            :class="[tier.featured ? 'text-emerald-400' : 'text-emerald-600', 'h-6 w-5 flex-none']"
                                            aria-hidden="true" />
                                        {{ feature }}
                                    </li>
                                </ul>
                                <router-link :to="{ name: 'courses' }"
                                    :aria-describedby="tier.id"
                                    :class="[tier.featured ? 'bg-emerald-500 text-white shadow-sm hover:bg-emerald-400 focus-visible:outline-emerald-500' : 'text-emerald-600 ring-1 ring-inset ring-emerald-200 hover:ring-emerald-300 focus-visible:outline-emerald-600', 'mt-8 block rounded-md px-3.5 py-2.5 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:mt-10']">
                                    Enroll now
                                </router-link>
                            </div>
                        </div>
                    </TabPanel>
                    <TabPanel value="1" as="p" class="m-0">
                        <div
                            class="mx-auto mt-16 grid max-w-7lg grid-cols-1 items-center gap-y-6 sm:mt-20 sm:gap-y-0 lg:max-w-7xl lg:grid-cols-4">
                            <!-- Pricing tiers -->
                            <div v-for="(tier, tierIdx) in tiers[1]?.tiers" :key="tier.id" :class="[
                                    'bg-white/60 sm:mx-8 lg:mx-0',
                                    tierIdx === 0 ? 'rounded-t-3xl sm:rounded-b-none lg:rounded-bl-3xl lg:rounded-tr-none' : '',
                                    tierIdx === 3 ? 'rounded-t-3xl sm:rounded-t-none lg:rounded-tr-3xl lg:rounded-bl-none' : '',
                                    tierIdx !== 0 && tierIdx !== 3 ? 'lg:rounded-none' : '',
                                    'rounded-3xl p-8 ring-1 ring-gray-900/10 sm:p-10'
                                ]">
                                <h3 :id="tier.id"
                                    :class="[tier.featured ? 'text-emerald-400' : 'text-emerald-600', 'text-base/7 font-semibold']">
                                    {{
                                    tier.name }}</h3>
                                <p class="mt-4 flex items-baseline gap-x-2">
                                    <span
                                        :class="[tier.featured ? 'text-white' : 'text-gray-900', 'text-5xl font-semibold tracking-tight']">
                                        ${{ Number(tier.price) }}</span>
                                    <span
                                        :class="[tier.featured ? 'text-gray-400' : 'text-gray-500', 'text-base']">/month</span>
                                </p>
                                <p :class="[tier.featured ? 'text-gray-300' : 'text-gray-600', 'mt-6 text-base/7']">
                                    {{
                                    tier.description
                                    }}</p>
                                <ul role="list"
                                    :class="[tier.featured ? 'text-gray-300' : 'text-gray-600', 'mt-8 space-y-3 text-sm/6 sm:mt-10']">
                                    <li v-for="feature in tier.features" :key="feature" class="flex gap-x-3">
                                        <CheckIcon
                                            :class="[tier.featured ? 'text-emerald-400' : 'text-emerald-600', 'h-6 w-5 flex-none']"
                                            aria-hidden="true" />
                                        {{ feature }}
                                    </li>
                                </ul>
                                <router-link :to="{ name: 'courses' }" :aria-describedby="tier.id"
                                    :class="[tier.featured ? 'bg-emerald-500 text-white shadow-sm hover:bg-emerald-400 focus-visible:outline-emerald-500' : 'text-emerald-600 ring-1 ring-inset ring-emerald-200 hover:ring-emerald-300 focus-visible:outline-emerald-600', 'mt-8 block rounded-md px-3.5 py-2.5 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:mt-10']">
                                    Enroll now
                                </router-link>
                            </div>
                        </div>
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </div>
    </div>
</template>

<script setup>
import { CheckIcon } from '@heroicons/vue/20/solid';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import { ref } from 'vue';
import { getTiers } from '@/services/tierService';

const value = ref('0');
const tiers = ref({});

getTiers().then((data) => {
    tiers.value = data.data;
});

</script>

<style>
.p-tabpanels {
    background-color: transparent !important;
}
</style>
